import React from "react";
import { FaLock, FaEye } from "react-icons/fa";
import { TeamAvatar, VoteCount, voteTargetClasses } from "./liveUi";

// Read-only mirror of the question board — markup copied from
// components/themes/Tarbaa.js (multi-mode and single-mode variants, team
// footer). Tiles are VOTE buttons for turn-team viewers; the GM decides.
const LiveBoard = ({ state, presence, voting }) => {
  const modes = state?.board?.modes || [];
  const answered = new Set((state?.answeredIds || []).map(String));
  const lowToHigh = !!state?.meta?.lowToHighPoints;
  const faded = !!state?.meta?.fadedQuestions;
  // Older app hosts keep hidden-points tiles blank and send an order that is
  // not their on-screen one, so a number here would point at the wrong tile.
  const numbered = !!state?.meta?.numberedTiles;
  const teams = state?.teams || [];
  const isSingleMode = modes.length === 1;

  const lastUnansweredIndex = (qs) => {
    const rev = [...qs].reverse().findIndex((q) => !answered.has(String(q.id)));
    return rev === -1 ? -1 : qs.length - 1 - rev;
  };

  const tileButton = (q, number, locked, heightClass) => {
    const isAnswered = answered.has(String(q.id));
    const key = `tile:${q.id}`;
    const votable = !isAnswered && !locked;
    const count = votable ? voting.counts?.[key] || 0 : 0;
    const mine = votable && voting.myVote === key;

    return (
      <div
        key={q.id}
        className={`${heightClass} flex justify-center items-center relative`}
      >
        <button
          onClick={() => votable && voting.tap("board", key)}
          disabled={!votable}
          className={`h-full w-full bg-[#bcc2d3] text-white rounded-lg text-center portrait:text-xl disabled:opacity-50 ${
            locked && !isAnswered ? "disabled:bg-[#474950]" : "disabled:bg-[#d6d9e1]"
          } ${votable ? voteTargetClasses(mine, voting.canVote) : ""}`}
        >
          <h1>{faded && !isAnswered ? numbered && number : q.points}</h1>
        </button>
        {votable && <VoteCount count={count} />}
        {locked && !isAnswered && (
          <div className="absolute w-8 h-8 flex justify-center items-center opacity-50">
            <FaLock className="text-dark size-6 xl:size-10" />
          </div>
        )}
      </div>
    );
  };

  const renderSingleMode = () => {
    const mode = modes[0];
    const qs = mode?.questions || [];
    const indexed = qs.map((q, idx) => ({ q, idx }));
    const leftQs = indexed.filter(({ idx }) => idx % 2 === 0);
    const rightQs = indexed.filter(({ idx }) => idx % 2 !== 0);
    const lastIdx = lastUnansweredIndex(qs);

    const card = ({ q, idx }) =>
      tileButton(
        q,
        idx + 1,
        lowToHigh && !answered.has(String(q.id)) && idx !== lastIdx,
        "w-1/3 h-1/6 p-1"
      );

    return (
      <div className="w-full h-full flex flex-row gap-1 md:text-xl xl:text-2xl font-bold">
        <div className="w-[40%] h-full flex flex-row flex-wrap content-center">
          {leftQs.map(card)}
        </div>
        <div
          className={`w-[30%] h-full p-1 relative flex justify-center items-end overflow-hidden rounded-lg ${
            mode?.isCustom ? "border-4 border-mainText" : ""
          }`}
        >
          <img
            src={mode?.image}
            alt={mode?.name}
            className="absolute inset-0 w-full h-full object-cover z-0"
          />
          <div className="z-10 w-full mb-1 bg-[#000000bb] rounded-lg flex justify-center items-center py-2">
            <h1 className="text-white text-center md:text-lg xl:text-xl">
              {mode?.name}
            </h1>
          </div>
        </div>
        <div className="w-[40%] h-full flex flex-row flex-wrap content-center">
          {rightQs.map(card)}
        </div>
      </div>
    );
  };

  return (
    <div className="w-full h-[89vh]">
      <div className="w-full h-[69vh] xl:h-[76vh] flex flex-row flex-wrap p-1 justify-center">
        {isSingleMode
          ? renderSingleMode()
          : modes.map((mode, modeIndex) => {
              const qs = mode.questions || [];
              const lastIdx = lastUnansweredIndex(qs);
              return (
                <div
                  key={mode.id}
                  className="w-1/3 h-1/2 sm:w-1/6 sm:h-full md:text-xl xl:text-2xl font-bold"
                >
                  <div
                    className={`relative w-full h-full p-1 ${
                      modeIndex !== 0 ? "border-l-[1px]" : ""
                    }`}
                  >
                    {/* MODE HEADER */}
                    <div
                      className={`relative h-2/6 flex justify-center items-end rounded-xl text-center ${
                        mode.isCustom ? "border-4 border-mainText" : ""
                      }`}
                    >
                      <div className="z-20 h-1/3 lg:h-1/4 flex justify-center items-center w-full bg-[#000000bb] rounded-lg">
                        <h1 className="text-white text-center md:text-lg xl:text-xl">
                          {mode.name}
                        </h1>
                      </div>
                      <div className="absolute w-full h-full z-0 overflow-hidden rounded-lg">
                        <img
                          src={mode.image}
                          alt={mode.name}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    </div>

                    {/* QUESTIONS */}
                    <div className="h-4/6">
                      {qs.map((q, qIndex) =>
                        tileButton(
                          q,
                          qIndex + 1,
                          lowToHigh &&
                            !answered.has(String(q.id)) &&
                            qIndex !== lastIdx,
                          "h-1/6 pt-1 xl:pt-3 px-1"
                        )
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
      </div>

      {/* TEAMS / FOOTER — same as themes/Tarbaa.js, ± buttons replaced by the
          per-team spectator count */}
      <div className="w-full h-[21vh] xl:h-[14vh] flex justify-center items-center overflow-hidden bg-brown border-t-[1vh] border-mainButtonShadow">
        <div className="h-full w-full flex flex-row 2xl:p-2 justify-center">
          {teams.map((team) => {
            const isTurn = String(team.id) === String(state?.turnTeamId);
            return (
              <div className="w-1/4 h-full px-[1px] lg:p-2" key={team.id}>
                <div
                  className={`w-full h-full flex flex-col sm:flex-row justify-center items-center bg-[#dd9c6e] rounded-lg ${
                    isTurn ? "border-4" : ""
                  }`}
                >
                  <div className="w-full h-1/2 sm:w-1/3 sm:h-full flex justify-center items-center p-1 sm:p-2">
                    <div className="h-full aspect-square rounded-full overflow-hidden flex justify-center items-center">
                      <div className="w-full aspect-square rounded-full overflow-hidden">
                        <TeamAvatar team={team} />
                      </div>
                    </div>
                  </div>
                  <div className="h-1/2 w-full sm:w-2/3 sm:h-full text-sm md:text-xl lg:text-2xl font-bold portrait:text-xl">
                    <div className="h-1/2 flex items-center justify-center overflow-hidden">
                      <h1 className="text-white text-center">{team.name}</h1>
                    </div>
                    <div className="w-full h-1/2 flex justify-between items-center p-2">
                      <span className="flex items-center gap-1 text-white/90 text-xs md:text-sm font-normal bg-mainButtonShadow rounded-lg px-2 py-1">
                        <FaEye className="size-3 xl:size-4" />
                        {presence?.byTeam?.[team.id] || 0}
                      </span>
                      <h1 className="text-white font-bold">{team.points}</h1>
                      <span className="w-6" />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default LiveBoard;
